export const portalOperations = [
  "portal.getMyClients",
  "portal.getTablePreferences",
  "portal.saveTablePreferences",
  "portal.supervisor.getTeamOverview",
  "portal.supervisor.getTeamClients",
  "portal.supervisor.getTeamClientDetail",
  "portal.supervisor.getTeamHomework",
  "portal.supervisor.getTeamHomeworkDetail",
  "portal.supervisor.getTeamReports",
  "portal.supervisor.getTeamAttention",
  "portal.getClient",
  "portal.getRecordFormConfig",
  "portal.getRelationOptions",
  "portal.createClient",
  "portal.updateClient",
  "portal.getClientValues",
  "portal.reports.getValueEngagement",
  "portal.reports.getHomeworkOverview",
  "portal.reports.getCompletionByDevice",
  "portal.reports.getDashboardAttention",
  "portal.askClinicianAssistant",
  "portal.createClientValue",
  "portal.updateClientValue",
  "portal.setClientValueEnabled",
  "portal.getRankLevels",
  "portal.getClientRankLevelRules",
  "portal.createClientRankLevelRule",
  "portal.updateClientRankLevelRule",
  "portal.setClientRankLevelRuleEnabled",
  "portal.getCopingSkills",
  "portal.createCopingSkill",
  "portal.updateCopingSkill",
  "portal.setCopingSkillEnabled",
  "portal.getClientCopingSkills",
  "portal.createClientCopingSkill",
  "portal.updateClientCopingSkill",
  "portal.setClientCopingSkillEnabled",
  "portal.getCurrentHomework",
  "portal.getHomeworkHistory",
  "portal.getClientSessions",
  "portal.getClientSessionTranscript",
  "portal.getHomeworkCalendar",
  "portal.getCaseloadEngagement",
  "portal.getDailyMoodChecks",
  "portal.getYouthTopProblemRatings",
  "portal.getExpectancies",
  "portal.getHomeworkAnalyses",
  "portal.generateHomeworkAnalysis",
  "portal.admin.getActivityRegistry",
  "portal.admin.getLovCatalogs",
  "portal.admin.getLovOptions",
  "portal.admin.createLovOption",
  "portal.admin.updateLovOption",
  "portal.admin.setLovOptionEnabled",
  "portal.admin.getClinics",
  "portal.admin.createClinic",
  "portal.admin.updateClinic",
  "portal.admin.getTherapists",
  "portal.admin.getAllClients",
  "portal.admin.createTherapist",
  "portal.admin.updateTherapist",
  "portal.getHomeworkDetail",
  "portal.getActivityTemplates",
  "portal.getActivityTemplate",
  "portal.getActivityTypes",
  "portal.createActivityTemplate",
  "portal.updateActivityTemplate",
  "portal.updateActivityTemplateSteps",
  "portal.uploadActivityTemplateImage",
  "portal.deleteActivityTemplateImage",
  "portal.uploadHomeworkImage",
  "portal.deleteHomeworkImage",
  "portal.getHomeworkActivities",
  "portal.createHomeworkActivity",
  "portal.createHomework",
  "portal.selectHomeworkTemplate",
  "portal.updateHomework",
  "portal.updateHomeworkSteps",
  "portal.duplicateHomework",
  "portal.deleteHomework",
] as const;

export type PortalOperation = (typeof portalOperations)[number];

export interface PortalTherapist {
  id: string;
  therapistName: string;
  email: string;
  clinicianCode: string;
  status: string;
  role: string;
  advancedDashboardEnabled: boolean;
  lastPortalActiveAt?: string | null;
  clinicNames?: string[];
}

export interface PortalPatient {
  id: string;
  accessCode: string;
  clinicId: string;
  nickname: string;
  status: string;
  clinicalSummary: string;
  highSudsThreshold: number;
  sudsInputStyle: string;
  selfInitiatedPermitted: boolean;
  copingStrategies: string[];
  funActivities: string[];
  pointsEarned: number;
  coins: number;
  badgeLevel: string;
  badgeCollection: string[];
}

export interface AdminClientClinician {
  id: string;
  therapistName: string;
  email: string;
  clinicianCode: string;
  status: string;
}

export interface AdminClientClinic {
  id: string;
  clinicName: string;
  status: string;
}

export interface AdminClientPatient {
  id: string;
  accessCode: string;
  nickname: string;
  clinician: AdminClientClinician | null;
  clinicianId: string;
  clinicianName: string;
  clinicianIds: string[];
  clinicianNames: string[];
  clinicians: AdminClientClinician[];
  clinic: AdminClientClinic | null;
  clinicId: string;
  clinicName: string;
  status: string;
  clinicalSummary: string;
  exposureHierarchyText: string;
  highSudsThreshold: number | null;
  sudsInputStyle: string;
  selfInitiatedPermitted: boolean;
  copingStrategies: string[];
  funActivities: string[];
  pointsEarned: number | null;
  coinsEarned: number | null;
  coins: number | null;
  rank: string;
  badgeLevel: string;
  badgeCollection: unknown[];
}

export type ClinicianAssistantPage =
  | "home"
  | "clients"
  | "client-profile"
  | "client-homework"
  | "client-insights"
  | "reports"
  | "homework-detail"
  | "supervisor-team"
  | "supervisor-team-client"
  | "supervisor-team-homework"
  | "supervisor-team-reports"
  | "other";

export type ClinicianAssistantScope =
  | { mode: "personal" }
  | {
      mode: "team";
      clinicId?: string;
      therapistId?: string;
    };

export interface ClinicianAssistantContext {
  page: ClinicianAssistantPage;
  patientId?: string;
  patientNickname?: string;
  homeworkAssignmentId?: string;
}

export interface ClinicianAssistantHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ClinicianAssistantEvidence {
  type: string;
  label: string;
  value?: string | number | null;
  occurredAt?: string | null;
  patientId?: string;
}

export type ClinicianAssistantLinkPage =
  | "client-profile"
  | "client-homework"
  | "client-insights"
  | "homework-detail"
  | "report"
  | "supervisor-team"
  | "supervisor-team-client"
  | "supervisor-team-homework"
  | "supervisor-team-reports";

export interface ClinicianAssistantLink {
  label: string;
  page: ClinicianAssistantLinkPage;
  patientId?: string;
  homeworkAssignmentId?: string;
  reportKey?: string;
}

export interface ClinicianAssistantResponse {
  success: true;
  found?: boolean;
  authorized: true;
  answer: string;
  generatedAt: string;
  needsClarification?: false;
  resolvedContext?: {
    patientId?: string;
    patientNickname?: string;
  };
  evidence?: ClinicianAssistantEvidence[];
  links?: ClinicianAssistantLink[];
}

export interface ClinicianAssistantClarification {
  success: true;
  needsClarification: true;
  message: string;
  choices: Array<{
    patientId: string;
    label: string;
  }>;
}

export type ClinicianAssistantResult =
  | ClinicianAssistantResponse
  | ClinicianAssistantClarification;

export type PortalFormFieldType =
  | "text"
  | "textarea"
  | "integer"
  | "number"
  | "boolean"
  | "lov"
  | "relation"
  | "date"
  | "datetime"
  | "email"
  | "url"
  | "attachment";

export interface LovCatalogWithOptions extends LovCatalog {
  options: LovOption[];
}

export interface PortalRecordFormField {
  id: string;
  recordType: string;
  fieldKey: string;
  label: string;
  fieldType: PortalFormFieldType;
  lovCatalogKey: string | null;
  relationSourceKey: string | null;
  displayOrder: number;
  enabled: boolean;
  visibleOnCreate: boolean;
  visibleOnEdit: boolean;
  editableOnCreate: boolean;
  editableOnEdit: boolean;
  required: boolean;
  defaultValue: unknown | null;
  minValue: number | null;
  maxValue: number | null;
  helpText: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  catalog: LovCatalogWithOptions | null;
}

export interface RecordFormConfigData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getRecordFormConfig";
  db: "supabase";
  therapist: PortalTherapist | null;
  recordType: "client";
  fieldCount: number;
  lovCatalogCount: number;
  resolvedLovCatalogCount: number;
  missingLovCatalogKeys: string[];
  fields: PortalRecordFormField[];
}

export interface PortalRelationOption {
  id: string;
  value: string;
  name: string;
  enabled: boolean;
  status: string;
}

export interface RelationOptionsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getRelationOptions";
  db: "supabase";
  therapist: PortalTherapist | null;
  recordType: "client";
  fieldKey: "clinicId";
  relationSourceKey: "clinics";
  options: PortalRelationOption[];
}

export interface ClientMutationFields {
  nickname?: string;
  clinicId?: string;
  status?: string;
  clinicalSummary?: string;
  highSudsThreshold?: number;
  sudsInputStyle?: string;
  selfInitiatedPermitted?: boolean;
  copingStrategies?: string[];
  funActivities?: string[];
  pointsEarned?: number;
  coins?: number;
  badgeCollection?: string[];
}

export type CreateClientFields = ClientMutationFields & {
  nickname: string;
  clinicId: string;
};
export type UpdateClientFields = ClientMutationFields;

export interface PortalClientMutationPatient extends PortalPatient {
  createdAt: string;
  updatedAt: string;
}

interface ClientMutationData {
  success: true;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalClientMutationPatient;
}

export interface CreateClientData extends ClientMutationData {
  created: true;
  operation: "portal.createClient";
}

export interface UpdateClientData extends ClientMutationData {
  updated: true;
  operation: "portal.updateClient";
}

export interface ClientValue {
  id: string;
  patientId: string;
  valueName: string;
  whyImportant: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientValuesData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getClientValues";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  values: ClientValue[];
  access?: ClientRecordAccess;
}

export interface ValueEngagementClient {
  patientId: string;
  accessCode: string;
  nickname: string;
}

export interface ValueEngagementFilters {
  patientId: string;
  accessCode: string;
  activityTemplateId: string | null;
  homeworkActivity: string;
}

export interface ValueEngagementActivityOption {
  id: string;
  name: string;
}

export interface ValueEngagementMetric {
  engagedCount: number;
  sampleSize: number;
  engagementRate: number | null;
}

export interface ValueEngagementMetrics {
  totalSampleSize: number;
  valueLinked: ValueEngagementMetric;
  noValueLinked: ValueEngagementMetric;
  differencePercentagePoints: number | null;
}

export type ValueEngagementCompletionStatus =
  | "Completed"
  | "Completed with Modification"
  | "Attempted"
  | "Missed";

export interface ValueEngagementAssignment {
  assignmentId: string;
  patientId: string;
  accessCode: string;
  nickname: string;
  activityTemplateId: string | null;
  homeworkActivity: string;
  valueLinked: boolean;
  valueSupportedId: string | null;
  valueSupportedName: string;
  completionStatus: ValueEngagementCompletionStatus;
  engaged: boolean;
  assignedAt: string;
  completedAt: string | null;
}

export interface ValueEngagementReportData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.reports.getValueEngagement";
  db: "supabase";
  therapist: PortalTherapist | null;
  client: ValueEngagementClient | null;
  report: "valueEngagement";
  generatedAt: string;
  filters: ValueEngagementFilters;
  activityOptions: ValueEngagementActivityOption[];
  metrics: ValueEngagementMetrics;
  assignments: ValueEngagementAssignment[];
}

export const homeworkOverviewPeriods = [
  "this-week",
  "last-30-days",
  "custom",
] as const;

export type HomeworkOverviewPeriod = (typeof homeworkOverviewPeriods)[number];

export interface HomeworkOverviewClientOption {
  patientId: string;
  nickname: string;
  accessCode: string;
  status: string;
}

export interface HomeworkOverviewFilters {
  patientId: string | null;
  period: HomeworkOverviewPeriod;
  startDate: string;
  endDate: string;
  dateBasis: "assigned";
}

export interface HomeworkOverviewSummary {
  assigned: number;
  completed: number;
  open: number;
  overdue: number;
  completionRate: number | null;
  selfInitiated: number;
}

export interface HomeworkOverviewClinicalMetrics {
  copingSkillAppliedCount: number;
  copingSkillAppliedDenominator: number;
  copingSkillAppliedRate: number | null;
  alteredRoutineCount: number;
  alteredRoutineDenominator: number;
  alteredRoutineRate: number | null;
  pairedSudsCount: number;
  averagePreSuds: number | null;
  averagePostSuds: number | null;
  averageSudsChange: number | null;
  routineTimeRecordedCount: number;
  averageRoutineTime: number | null;
  medianRoutineTime: number | null;
  routineTimeUnit: "minutes";
}

export interface HomeworkOverviewReportData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.reports.getHomeworkOverview";
  db: "supabase";
  therapist: PortalTherapist | null;
  report: "homeworkOverview";
  generatedAt: string;
  timezone: "America/Los_Angeles";
  asOfDate: string;
  filters: HomeworkOverviewFilters;
  clientOptions: HomeworkOverviewClientOption[];
  summary: HomeworkOverviewSummary;
  clinicalMetrics: HomeworkOverviewClinicalMetrics;
}

export const completionByDevicePeriods = [
  "this-week",
  "last-30-days",
  "custom",
] as const;

export type CompletionByDevicePeriod =
  (typeof completionByDevicePeriods)[number];

export interface CompletionByDeviceClientOption {
  patientId: string;
  nickname: string;
  accessCode: string;
  status: string;
}

export interface CompletionByDeviceActivityOption {
  id: string;
  name: string;
}

export interface CompletionByDeviceFilters {
  patientId: string | null;
  activityTemplateId: string | null;
  period: CompletionByDevicePeriod;
  startDate: string;
  endDate: string;
  dateBasis: "completed";
}

export type CompletionByDeviceKey =
  | "desktop_laptop"
  | "iphone"
  | "android_phone"
  | "ipad"
  | "android_tablet"
  | "unknown";

export interface CompletionByDeviceSummary {
  completedAssignments: number;
  knownDeviceAssignments: number;
  unknownDeviceAssignments: number;
}

export interface CompletionByDeviceBreakdownItem {
  deviceKey: CompletionByDeviceKey;
  deviceLabel: string;
  completedCount: number;
  percentage: number;
}

export interface CompletionByDeviceReportData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.reports.getCompletionByDevice";
  db: "supabase";
  therapist: PortalTherapist | null;
  report: "completionByDevice";
  generatedAt: string;
  timezone: "America/Los_Angeles";
  asOfDate: string;
  filters: CompletionByDeviceFilters;
  clientOptions: CompletionByDeviceClientOption[];
  activityOptions: CompletionByDeviceActivityOption[];
  summary: CompletionByDeviceSummary;
  deviceBreakdown: CompletionByDeviceBreakdownItem[];
}

export interface DashboardAttentionWindow {
  range: "last_7_days";
  startDate: string;
  endDate: string;
}

export interface DashboardAttentionSummary {
  activatedClients: number;
  newClients: number;
  clientsOnTrack: number;
  clientsNeedingFollowUp: number;
  clientsWithNoRecentAssistantUse: number;
  clientsWithSafetyEvents: number;
  totalSafetyDetections: number;
  sessionsWithSafetyDetections: number;
  clientsWithNoActivityLast7Days: number;
  clientsWithNoActivityThisWeek: number;
}

export const dashboardAttentionEngagementStatuses = [
  "new-client",
  "on-track",
  "needs-follow-up",
  "no-recent-assistant-use",
] as const;

export type DashboardAttentionEngagementStatus =
  (typeof dashboardAttentionEngagementStatuses)[number];

export interface DashboardAttentionHomeworkCompletionBasis {
  range: "all_time";
  denominator: "all_assignments";
}

export interface DashboardAttentionEngagementClient {
  clientId: string;
  accessCode: string;
  nickname: string;
  engagementStatus: DashboardAttentionEngagementStatus;
  reasonCode: string;
  lastAssistantUseAt: string | null;
  daysSinceAssistantUse: number | null;
  assistantInteractionsLast7Days: number;
  homeworkAssignedCount: number;
  homeworkCompletedCount: number;
  completionRate: number | null;
}

export interface DashboardAttentionEventType {
  safetyEventType: string;
  count: number;
}

export interface DashboardAttentionSafetyClient {
  clientId: string;
  accessCode: string;
  nickname: string;
  safetyDetectionCount: number;
  sessionsWithSafetyDetections: number;
  lastSafetyEventAt: string;
  eventTypes: DashboardAttentionEventType[];
}

export interface DashboardAttentionInactiveClient {
  clientId: string;
  accessCode: string;
  nickname: string;
  status: string;
  lastActiveDate: string | null;
  daysSinceActive: number | null;
}

export interface DashboardAttentionReportData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.reports.getDashboardAttention";
  db: "supabase";
  therapist: PortalTherapist | null;
  report: "dashboardAttention";
  generatedAt: string;
  timezone: "America/Los_Angeles";
  asOfDate: string;
  safetyWindow: DashboardAttentionWindow & { range: "last_7_days" };
  assistantUseWindow: DashboardAttentionWindow & { range: "last_7_days" };
  inactivityWindow: DashboardAttentionWindow & { range: "last_7_days" };
  homeworkCompletionBasis: DashboardAttentionHomeworkCompletionBasis;
  summary: DashboardAttentionSummary;
  engagementClients: DashboardAttentionEngagementClient[];
  safetyClients: DashboardAttentionSafetyClient[];
  inactiveClients: DashboardAttentionInactiveClient[];
}

export interface SupervisorTeamAttentionSummary {
  activatedClients: number;
  clientsNeedingAttention: number;
  clientsNeedingFollowUp: number;
  clientsWithNoRecentAssistantUse: number;
  clientsWithNoHomework: number;
  clientsOnTrack: number;
  clientsWithSafetyEvents: number;
  totalSafetyDetections: number;
}

export interface SupervisorTeamAttentionEngagementClient
  extends DashboardAttentionEngagementClient {
  clinicId: string;
  clinicName: string;
  assignedTherapists: SupervisorAssignedTherapist[];
}

export interface SupervisorTeamAttentionSafetyClient
  extends DashboardAttentionSafetyClient {
  clinicId: string;
  clinicName: string;
  assignedTherapists: SupervisorAssignedTherapist[];
}

export interface SupervisorTeamAttentionClient
  extends SupervisorTeamAttentionEngagementClient {
  reasons: string[];
}

export interface SupervisorTeamAttentionData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamAttention";
  db: "supabase";
  therapist: PortalTherapist | null;
  clinicOptions: SupervisorClinicOption[];
  clinics: SupervisorClinicSummary[];
  teamMembers: SupervisorTeamMember[];
  clients: SupervisorTeamClient[];
  generatedAt: string;
  timezone: "America/Los_Angeles";
  asOfDate: string;
  engagementClients: SupervisorTeamAttentionEngagementClient[];
  safetyClients: SupervisorTeamAttentionSafetyClient[];
  attentionClients: SupervisorTeamAttentionClient[];
  summary: SupervisorTeamAttentionSummary;
}

interface ClientValueMutationData {
  success: true;
  db: "supabase";
  value: ClientValue;
}

export interface CreateClientValueData extends ClientValueMutationData {
  created: true;
  operation: "portal.createClientValue";
}

export interface UpdateClientValueData extends ClientValueMutationData {
  updated: true;
  operation: "portal.updateClientValue";
}

export interface SetClientValueEnabledData extends ClientValueMutationData {
  updated: true;
  operation: "portal.setClientValueEnabled";
}

export interface RankLevel {
  id: string;
  key: string;
  name: string;
  sortOrder: number;
  enabled: boolean;
}

export interface ClientRankLevelRule {
  id: string;
  patientId: string;
  rankLevelId: string;
  rankLevelKey: string;
  rankLevelName: string;
  thresholdPoints: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RankLevelsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getRankLevels";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  rankLevels: RankLevel[];
  access?: ClientRecordAccess;
}

export interface ClientRankLevelRulesData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getClientRankLevelRules";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  rules: ClientRankLevelRule[];
  access?: ClientRecordAccess;
}

export interface CreateClientRankLevelRuleFields {
  rankLevelId: string;
  thresholdPoints: number;
}

export interface UpdateClientRankLevelRuleFields {
  rankLevelId?: string;
  thresholdPoints?: number;
}

interface ClientRankLevelRuleMutationData {
  success: true;
  found: true;
  authorized: true;
  db: "supabase";
  therapist: PortalTherapist;
  patient: PortalPatient;
  rule: ClientRankLevelRule;
}

export interface CreateClientRankLevelRuleData
  extends ClientRankLevelRuleMutationData {
  created: true;
  operation: "portal.createClientRankLevelRule";
}

export interface UpdateClientRankLevelRuleData
  extends ClientRankLevelRuleMutationData {
  updated: true;
  operation: "portal.updateClientRankLevelRule";
}

export interface SetClientRankLevelRuleEnabledData
  extends ClientRankLevelRuleMutationData {
  updated: true;
  operation: "portal.setClientRankLevelRuleEnabled";
}

export interface CopingSkillGuide {
  id: string;
  copingStrategyOptionId: string;
  copingStrategyKey: string;
  copingStrategyValue: string;
  skillKey: string;
  skillName: string;
  patientId: string | null;
  guideSteps: string;
  enabled: boolean;
  copingStrategyEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CopingSkillsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getCopingSkills";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  guides: CopingSkillGuide[];
  access?: ClientRecordAccess;
}

export interface ClientCopingSkillsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getClientCopingSkills";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  guides: CopingSkillGuide[];
  access?: ClientRecordAccess;
}

export interface CreateCopingSkillFields {
  copingStrategyOptionId: string;
  guideSteps: string;
}

export interface UpdateCopingSkillFields {
  copingStrategyOptionId?: string;
  guideSteps?: string;
}

interface CopingSkillMutationData {
  success: true;
  found: true;
  authorized: true;
  db: "supabase";
  therapist: PortalTherapist;
  patient: PortalPatient | null;
  guide: CopingSkillGuide;
}

export interface CreateCopingSkillData extends CopingSkillMutationData {
  created: true;
  operation: "portal.createCopingSkill";
  patient: null;
}

export interface UpdateCopingSkillData extends CopingSkillMutationData {
  updated: true;
  operation: "portal.updateCopingSkill";
  patient: null;
}

export interface SetCopingSkillEnabledData extends CopingSkillMutationData {
  updated: true;
  operation: "portal.setCopingSkillEnabled";
  patient: null;
}

export interface CreateClientCopingSkillData extends CopingSkillMutationData {
  created: true;
  operation: "portal.createClientCopingSkill";
  patient: PortalPatient;
}

export interface UpdateClientCopingSkillData extends CopingSkillMutationData {
  updated: true;
  operation: "portal.updateClientCopingSkill";
  patient: PortalPatient;
}

export interface SetClientCopingSkillEnabledData
  extends CopingSkillMutationData {
  updated: true;
  operation: "portal.setClientCopingSkillEnabled";
  patient: PortalPatient;
}

export interface HomeworkEngagement {
  engagementRate: number;
  homeworkCompleted: number;
  totalAssigned: number;
}

export interface MyClientsPageData {
  success: true;
  found: boolean;
  operation: "portal.getMyClients";
  db: "supabase";
  therapist: PortalTherapist | null;
  patients: PortalPatient[];
}

export type SupervisorMembershipRole = "therapist" | "supervisor";

export interface SupervisorTeamMembership {
  clinicId: string;
  clinicName: string;
  membershipRole: SupervisorMembershipRole;
}

export interface SupervisorTeamMember {
  id: string;
  therapistName: string;
  email: string;
  clinicianCode: string;
  accountRole: string;
  status: string;
  isCurrentUser: boolean;
  memberships: SupervisorTeamMembership[];
}

export interface SupervisorClinicOption {
  id: string;
  clinicName: string;
  status: string;
}

export interface SupervisorClinicSummary extends SupervisorClinicOption {
  teamMemberCount?: number;
  supervisorCount?: number;
  therapistCount?: number;
}

export interface SupervisorTeamSummary {
  clinicCount: number;
  teamMemberCount: number;
  supervisorCount: number;
  therapistCount: number;
}

export interface SupervisorTeamOverviewData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamOverview";
  db: "supabase";
  therapist: PortalTherapist | null;
  clinicOptions: SupervisorClinicOption[];
  clinics: SupervisorClinicSummary[];
  teamMembers: SupervisorTeamMember[];
  summary: SupervisorTeamSummary;
}

export interface SupervisorAssignedTherapist {
  id: string;
  therapistName: string;
}

export interface SupervisorClientClinic {
  id: string;
  clinicName: string;
}

export type ClientAccessScope = "assigned" | "team";

export interface ClientRecordAccess {
  scope: ClientAccessScope;
  readOnly: boolean;
}

export interface SupervisorTeamClient extends PortalPatient {
  clinicName: string;
  assignedTherapists: SupervisorAssignedTherapist[];
}

export interface SupervisorTeamClientsSummary {
  clientCount: number;
  activatedClientCount: number;
}

export interface SupervisorTeamClientsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamClients";
  db: "supabase";
  therapist: PortalTherapist | null;
  clinicOptions: SupervisorClinicOption[];
  clinics: SupervisorClinicSummary[];
  teamMembers: SupervisorTeamMember[];
  clients: SupervisorTeamClient[];
  summary: SupervisorTeamClientsSummary;
}

export interface SupervisorTeamClientDetailData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamClientDetail";
  db: "supabase";
  therapist: PortalTherapist | null;
  client: SupervisorTeamClient | null;
  clinic: SupervisorClientClinic | null;
  assignedClinicians: SupervisorAssignedTherapist[];
  access: ClientRecordAccess | null;
}

export interface SupervisorTeamHomeworkAssignment
  extends PortalHomeworkActivitySummary {
  clinicId: string;
  clinicName: string;
  clinicianId: string;
  clinicianName: string;
}

export interface SupervisorTeamHomeworkSummary {
  assignmentCount: number;
  notCompletedCount: number;
}

export interface SupervisorTeamHomeworkData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamHomework";
  db: "supabase";
  therapist: PortalTherapist | null;
  clinicOptions: SupervisorClinicOption[];
  clinics: SupervisorClinicSummary[];
  teamMembers: SupervisorTeamMember[];
  clients: SupervisorTeamClient[];
  assignments: SupervisorTeamHomeworkAssignment[];
  summary: SupervisorTeamHomeworkSummary;
}

export interface SupervisorTeamHomeworkDetailData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamHomeworkDetail";
  db: "supabase";
  therapist: PortalTherapist | null;
  client: SupervisorTeamClient | null;
  clinician: SupervisorTeamMember | null;
  homework: PortalHomeworkDetail | null;
}

export interface SupervisorTeamReportAssignment {
  id: string;
  patientId: string;
  clinicId: string;
  assignedTherapistIds: string[];
  createdByTherapistId: string | null;
  activityTemplateId: string | null;
  homeworkName: string;
  completionStatus: string;
  dueDate: string | null;
  assignedAt: string;
  completedAt: string | null;
  firstSuds: number | null;
  secondSuds: number | null;
  copingSkills: string;
  altered: string;
  routineTime: string;
  deviceType: string;
  valueSupportedId: string | null;
}

export interface SupervisorTeamMessageEvent {
  patientId: string;
  occurredAt: string;
}

export interface SupervisorTeamReportsSummary {
  assignmentCount: number;
  userMessageCount: number;
}

export interface SupervisorTeamReportsData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.supervisor.getTeamReports";
  db: "supabase";
  therapist: PortalTherapist | null;
  timezone: "America/Los_Angeles";
  generatedAt: string;
  clinicOptions: SupervisorClinicOption[];
  clinics: SupervisorClinicSummary[];
  teamMembers: SupervisorTeamMember[];
  clients: SupervisorTeamClient[];
  assignments: SupervisorTeamReportAssignment[];
  messageEvents: SupervisorTeamMessageEvent[];
  summary: SupervisorTeamReportsSummary;
}

export interface ClientProfilePageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getClient";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homeworkEngagement?: HomeworkEngagement;
  access?: ClientRecordAccess;
}

export interface CurrentHomeworkAssignment {
  id: string;
  homeworkName: string;
  activityType: string;
  valueSupportedName?: string;
  activitySteps: string;
  points: number;
  coins: number;
  completionStatus: string;
  dueDate: string | null;
  overdue: boolean;
  isRecurring: boolean;
  repeats: "" | "Daily" | "Weekly";
  assignedDate: string;
  createdAt: string;
}

export interface CurrentHomeworkTemplateOption {
  id: string;
  name: string;
  status: string;
}

export interface CurrentHomeworkPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getCurrentHomework";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  assignments: CurrentHomeworkAssignment[];
  /** Legacy response field. Template selectors use portal.getActivityTemplates. */
  templates: CurrentHomeworkTemplateOption[];
  access?: ClientRecordAccess;
}

export const clientSessionFilters = ["past-week", "all"] as const;
export type ClientSessionFilter = (typeof clientSessionFilters)[number];

export interface ClientSession {
  sessionId: string;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
}

export interface ClientSessionsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getClientSessions";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  sessionFilter: ClientSessionFilter;
  pageSize: number;
  pageOffset: number;
  generatedAt: string;
  sessions: ClientSession[];
  hasMore: boolean;
  nextOffset: number | null;
}

export interface ClientTranscriptMessage {
  id: string;
  sender: string;
  message: string | null;
  sentAt: string;
}

export interface ClientSessionTranscriptData {
  success: true;
  found: true;
  authorized: true;
  operation: "portal.getClientSessionTranscript";
  db: "supabase";
  therapist: PortalTherapist;
  patient: PortalPatient;
  sessionId: string;
  session: ClientSession;
  messages: ClientTranscriptMessage[];
  pageSize: number;
  pageOffset: number;
  generatedAt: string;
  hasMore: boolean;
  nextOffset: number | null;
}

export const homeworkHistoryFilters = [
  "completed-last-30-days",
  "completed-with-modification",
  "attempted",
  "missed",
  "completed-all",
  "all-activities",
] as const;

export type HomeworkHistoryFilter = (typeof homeworkHistoryFilters)[number];

export type HomeworkHistoryStatus =
  | "Completed"
  | "Completed with Modification"
  | "Attempted"
  | "Missed";

export interface HomeworkHistoryAssignment {
  id: string;
  homeworkName: string;
  activityType: string;
  valueSupportedName?: string;
  activitySteps: string;
  completionStatus: HomeworkHistoryStatus;
  completedAt: string | null;
  dueDate: string | null;
  overdue?: boolean;
  firstSuds: number | null;
  secondSuds: number | null;
  routineTime: string;
  emailAlert: boolean;
  assignedDate: string;
  createdAt: string;
  conversationReportUrl: string;
}

export interface HomeworkHistoryPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getHomeworkHistory";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  historyFilter: HomeworkHistoryFilter;
  assignments: HomeworkHistoryAssignment[];
  access?: ClientRecordAccess;
}

export const homeworkCalendarStatuses = [
  "Completed",
  "Attempted",
  "Due",
  "Missed",
] as const;

export type HomeworkCalendarStatus = (typeof homeworkCalendarStatuses)[number];

export interface HomeworkCalendarAssignment {
  recordId: string;
  activityName: string;
  activityTypeId: string;
  activityType: string;
  completionStatus: string;
  calendarStatus: HomeworkCalendarStatus;
  calendarDate: string;
  dueDate: string;
  completedAttemptedDate: string;
  suds1: number | null;
  suds2: number | null;
  points: number;
  coins: number;
}

export interface UnscheduledHomeworkAssignment {
  recordId: string;
  activityName: string;
  activityTypeId: string;
  activityType: string;
  completionStatus: "Not Completed";
  dueDate: "";
  points: number;
  coins: number;
}

export interface HomeworkCalendarPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getHomeworkCalendar";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  timezone: "America/Los_Angeles";
  range: {
    startDate: string;
    endDate: string;
  };
  homework: HomeworkCalendarAssignment[];
  count: number;
  unscheduledHomework: UnscheduledHomeworkAssignment[];
  unscheduledCount: number;
  access?: ClientRecordAccess;
}

export const caseloadEngagementPeriods = ["this-week", "last-30-days"] as const;

export type CaseloadEngagementPeriod =
  (typeof caseloadEngagementPeriods)[number];

export interface CaseloadEngagementSummary {
  eligibleClientCount: number;
  clientsWithMessageActivity: number;
  clientsWithHomeworkActivity: number;
  clientsWithNoActivity: number;
}

export interface CaseloadMessageClient {
  clientId: string;
  nickname: string;
  accessCode: string;
  userMessageCount: number;
}

export interface CaseloadHomeworkClient {
  clientId: string;
  nickname: string;
  accessCode: string;
  averageHomeworkSeconds: number | null;
  completedHomeworkCount: number;
  completedHomeworkSeconds: number | null;
  assignmentsWithRecordedDuration: number;
}

export interface CaseloadNoActivityClient {
  clientId: string;
  nickname: string;
  accessCode: string;
  status: string;
  lastActiveDate: string | null;
  daysSinceActive: number | null;
}

export interface CaseloadEngagementReport {
  period: CaseloadEngagementPeriod;
  timezone: string;
  asOfDate: string;
  windowStartDate: string;
  windowEndDate: string;
  generatedAt: string;
  summary: CaseloadEngagementSummary;
  topClientsByMessages: CaseloadMessageClient[];
  topClientsByCompletedHomework: CaseloadHomeworkClient[];
  clientsWithNoActivity: CaseloadNoActivityClient[];
}

export interface CaseloadEngagementPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getCaseloadEngagement";
  db: "supabase";
  therapist: PortalTherapist | null;
  period: CaseloadEngagementPeriod;
  caseloadEngagement: CaseloadEngagementReport;
}

export interface DailyMoodCheck {
  id: string;
  recordedOn: string;
  moodRating: number | null;
  ocdThoughtsObsessions: number | null;
  ocdCompulsionsRituals: number | null;
}

export interface DailyMoodChecksPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getDailyMoodChecks";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  moodChecks: DailyMoodCheck[];
  access?: ClientRecordAccess;
}

export interface YouthTopProblemRating {
  id: string;
  recordedOn: string;
  problem1Rating: number | null;
  problem2Rating: number | null;
  problem3Rating: number | null;
}

export interface YouthTopProblemRatingsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getYouthTopProblemRatings";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  respondentType: "client";
  ratings: YouthTopProblemRating[];
  access?: ClientRecordAccess;
}

export const expectancyPredictionResults = [
  "",
  "Pending",
  "Proved false",
  "Came true",
  "Partly true",
  "Not needed",
] as const;

export type ExpectancyPredictionResult =
  (typeof expectancyPredictionResults)[number];

export interface ExpectancyEntry {
  id: string;
  patientId: string;
  homeworkAssignmentId: string;
  exposure: string;
  obsession: string;
  timeSinceExposure: string;
  predictionResult: ExpectancyPredictionResult;
  whatILearned: string;
  createdAt: string;
  createdByUser: string;
}

export interface ExpectanciesPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getExpectancies";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  expectancies: ExpectancyEntry[];
  access?: ClientRecordAccess;
}

export interface PortalHomeworkAnalysis {
  id: string;
  analysis: string;
  triggerSource: string;
  createdAt: string;
}

export interface HomeworkAnalysesPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getHomeworkAnalyses";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  analyses: PortalHomeworkAnalysis[];
  access?: ClientRecordAccess;
}

export interface GenerateHomeworkAnalysisData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.generateHomeworkAnalysis";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  generated: true;
  recordCount: number;
  analysis: PortalHomeworkAnalysis;
}

export interface PortalActivityRegistryItem {
  id: string;
  activityKey: string;
  label: string;
  description: string;
  buddyType: string;
  buddyOrder: number | null;
  htmlFileName: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityRegistryPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getActivityRegistry";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  activities: PortalActivityRegistryItem[];
}

export interface LovCatalog {
  id: string;
  key: string;
  name: string;
  selectionType: string;
  orderingMode: LovOrderingMode;
  storageProvider: string;
  description: string;
  sortOrder: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export type LovOrderingMode = "fixed" | "alphabetical";

export interface LovOption {
  id: string;
  key: string;
  value: string;
  name: string;
  colorKey: string;
  sortOrder: number;
  enabled: boolean;
  isDefault: boolean;
  collection: string | null;
  assetPath: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LovCatalogsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getLovCatalogs";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  catalogs: LovCatalog[];
}

export interface LovOptionsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getLovOptions";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  catalogKey: string;
  catalog: LovCatalog;
  options: LovOption[];
}

export interface CreateLovOptionFields {
  key: string;
  value?: string;
  name: string;
  colorKey: string;
  sortOrder: number;
  enabled: boolean;
  isDefault: boolean;
  collection?: string;
  assetPath?: string;
}

export interface UpdateLovOptionFields {
  name?: string;
  colorKey?: string;
  sortOrder?: number;
  isDefault?: boolean;
  collection?: string;
  assetPath?: string;
}

interface LovOptionWriteData {
  success: true;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  catalogKey: string;
  catalog: LovCatalog;
  option: LovOption;
}

export interface CreateLovOptionData extends LovOptionWriteData {
  created: true;
  operation: "portal.admin.createLovOption";
}

export interface UpdateLovOptionData extends LovOptionWriteData {
  updated: true;
  operation: "portal.admin.updateLovOption";
}

export interface SetLovOptionEnabledData extends LovOptionWriteData {
  updated: true;
  operation: "portal.admin.setLovOptionEnabled";
}

export interface TherapistsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getTherapists";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  therapists: PortalTherapist[];
}

export interface AdminClientsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getAllClients";
  db: "supabase";
  therapist: PortalTherapist | null;
  patients: AdminClientPatient[];
  totalCount?: number;
}

export type AdminTherapistRole = "therapist" | "supervisor" | "admin";

export interface CreatedPortalTherapist extends Omit<PortalTherapist, "role"> {
  role: AdminTherapistRole;
  clinicIds: string[];
  clinicCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTherapistData {
  success: true;
  found: boolean;
  authorized: boolean;
  created: true;
  operation: "portal.admin.createTherapist";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  createdTherapist: CreatedPortalTherapist;
}

export interface UpdateTherapistFields {
  therapistName: string;
  email: string;
  role: AdminTherapistRole;
  status: string;
  clinicIds: string[];
  advancedDashboardEnabled: boolean;
}

export interface UpdateTherapistData {
  success: true;
  found: boolean;
  authorized: boolean;
  updated: true;
  operation: "portal.admin.updateTherapist";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  updatedTherapist: CreatedPortalTherapist;
}

export interface AdminClinic {
  id: string;
  clinicName: string;
  status: string;
  therapistCount: number;
  therapistIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ClinicsPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.admin.getClinics";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  clinics: AdminClinic[];
}

export interface CreateClinicData {
  success: true;
  found: boolean;
  authorized: boolean;
  created: true;
  operation: "portal.admin.createClinic";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  clinic: AdminClinic;
}

export interface UpdateClinicFields {
  clinicName: string;
  status: string;
  therapistIds: string[];
}

export interface UpdateClinicData {
  success: true;
  found: boolean;
  authorized: boolean;
  updated: true;
  operation: "portal.admin.updateClinic";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  clinic: AdminClinic;
}

export interface PortalActivityImage {
  id: string;
  filename: string;
  url: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number | null;
  sortOrder: number;
}

export interface PortalActivityTemplateSummary {
  id: string;
  name: string;
  activityType: string;
  activitySteps: string;
  coachingPrompts: string;
  alternative: string;
  points: number;
  coins: number;
  nonExposureHomework: boolean;
  status: string;
  updatedAt?: string | null;
}

export type ActivityTemplateStatus = "On" | "Off";

export interface PortalActivityType {
  id: string;
  key: string;
  name: string;
  colorKey: string;
  sortOrder: number;
  enabled: boolean;
}

export interface ActivityTypesPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getActivityTypes";
  db: "supabase";
  therapist: PortalTherapist | null;
  activityTypes: PortalActivityType[];
}

export interface ActivityTemplateEditableFields {
  name: string;
  description: string;
  activityTypeId: string;
  status: ActivityTemplateStatus;
  nonExposureHomework: boolean;
  activitySteps: string;
  alternative: string;
  coachingPrompts: string;
  points: number;
  coins: number;
}

export interface ActivityTemplatesPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getActivityTemplates";
  db: "supabase";
  therapist: PortalTherapist | null;
  templates: PortalActivityTemplateSummary[];
}

export interface PortalActivityTemplateRecord
  extends Omit<PortalActivityTemplateSummary, "status">,
    ActivityTemplateEditableFields {
  protected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PortalActivityTemplateDetail
  extends PortalActivityTemplateRecord {
  activityImages: PortalActivityImage[];
}

export interface ActivityTemplateDetailPageData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  operation: "portal.getActivityTemplate";
  db: "supabase";
  therapist: PortalTherapist | null;
  template: PortalActivityTemplateDetail | null;
  error?: string;
}

interface ActivityTemplateMutationData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  template: PortalActivityTemplateDetail | null;
  error?: string;
}

export interface CreateActivityTemplateData
  extends ActivityTemplateMutationData {
  operation: "portal.createActivityTemplate";
  created: boolean;
}

export interface UpdateActivityTemplateData
  extends ActivityTemplateMutationData {
  operation: "portal.updateActivityTemplate";
  updated: boolean;
}

export interface ActivityStepsMutationRecord {
  id: string;
  activitySteps: string;
}

export interface UpdateActivityTemplateStepsData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  operation: "portal.updateActivityTemplateSteps";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: null;
  template: ActivityStepsMutationRecord | null;
  error?: string;
}

export interface ActivityTemplateImageUploadFields {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentBase64: string;
}

interface ActivityTemplateImageMutationData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  templateId: string;
  error?: string;
}

export interface UploadActivityTemplateImageData
  extends ActivityTemplateImageMutationData {
  operation: "portal.uploadActivityTemplateImage";
  uploaded: boolean;
  image: PortalActivityImage | null;
}

export interface DeleteActivityTemplateImageData
  extends ActivityTemplateImageMutationData {
  operation: "portal.deleteActivityTemplateImage";
  deleted: boolean;
  imageId: string;
}

export interface PortalHomeworkActivitySummary {
  id: string;
  patientId: string;
  accessCode: string;
  nickname: string;
  patientStatus: string;
  activityTemplateId: string;
  homeworkName: string;
  activityType: string;
  activitySteps: string;
  completionStatus: string;
  dueDate: string | null;
  completedAt: string | null;
  assignedDate: string;
  createdAt: string;
  updatedAt: string;
  points: number;
  coins: number;
  nonExposureHomework: boolean;
  firstSuds: number | null;
  secondSuds: number | null;
  copingSkills: string;
  altered: string;
}

export interface PortalHomeworkActivitiesListAssignment
  extends PortalHomeworkActivitySummary {
  routineTime: string;
  emailAlert: boolean;
  valueSupportedName: string;
  conversationReportUrl: string;
}

export interface HomeworkActivitiesPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getHomeworkActivities";
  db: "supabase";
  therapist: PortalTherapist | null;
  assignments: PortalHomeworkActivitiesListAssignment[];
}

export type PortalTableKey =
  | "my_clients"
  | "homework_activities"
  | "current_homework"
  | "homework_history";

export interface PortalTablePreference {
  tableKey: PortalTableKey;
  columnOrder: string[];
  hiddenColumns: string[];
  columnWidths: Record<string, number>;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface TablePreferencesData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getTablePreferences" | "portal.saveTablePreferences";
  db: "supabase";
  therapist: PortalTherapist | null;
  tableKey: PortalTableKey;
  configured: boolean;
  preference: PortalTablePreference | null;
  saved?: boolean;
}

export const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export type Weekday = (typeof weekdays)[number];

export const homeworkCompletionStatuses = [
  "Not Completed",
  "Completed",
  "Completed with Modification",
  "Attempted",
  "Missed",
] as const;

export type HomeworkCompletionStatus =
  (typeof homeworkCompletionStatuses)[number];

export type HomeworkAsset = PortalActivityImage;

export interface PortalHomeworkDetail {
  id: string;
  homeworkAssignmentId: string;
  activityTemplateId: string;
  homeworkName: string;
  activityTypeId: string;
  activityType: string;
  valueSupportedId: string;
  nonExposureHomework: boolean;
  activitySteps: string;
  alternativeSteps: string;
  alternative: string;
  coachingPrompts: string;
  activityImages: HomeworkAsset[];
  points: number;
  coins: number;
  assignedDate: string;
  dueDate: string;
  isRecurring: boolean;
  repeatsUi: string;
  repeats: "" | "Daily" | "Weekly";
  repeatOn: Weekday[];
  recurrenceEndDate: string | null;
  completionStatus: HomeworkCompletionStatus;
  completedAt: string | null;
  completedDate: string | null;
  summary: string;
  firstSuds: number | "";
  secondSuds: number | "";
  sentimentAnalysis: string;
  copingSkills: string;
  altered: string;
  routineTime: string;
  clientUploads: HomeworkAsset[];
  createdAt: string;
  updatedAt: string;
  overdue: boolean;
  emailAlert: boolean;
  conversationReportUrl: string;
}

export interface HomeworkDetailPageData {
  success: true;
  found: boolean;
  authorized: boolean;
  operation: "portal.getHomeworkDetail";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homework: PortalHomeworkDetail | null;
  access?: ClientRecordAccess;
}

export interface PortalMutationHomework {
  id: string;
}

interface HomeworkMutationBase {
  success: boolean;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homework: PortalMutationHomework | null;
  error?: string;
}

export interface CreateHomeworkData extends HomeworkMutationBase {
  operation: "portal.createHomework";
}

export interface CreateHomeworkActivityFields {
  templateId: string;
  homeworkName: string;
  activityTypeId: string;
  valueSupportedId: string;
  nonExposureHomework: boolean;
  activitySteps: string;
  alternative: string;
  coachingPrompts: string;
  points: number;
  coins: number;
  dueDate: string;
  routineTime: string;
  isRecurring: boolean;
  repeats: "Daily" | "Weekly" | "";
  repeatOn: Weekday[];
  recurrenceEndDate: string;
}

export interface CreateHomeworkActivityData extends HomeworkMutationBase {
  operation: "portal.createHomeworkActivity";
  created: boolean;
}

export interface SelectHomeworkTemplateData extends HomeworkMutationBase {
  operation: "portal.selectHomeworkTemplate";
  template: { id: string } | null;
}

export interface HomeworkUpdateFields {
  valueSupportedId?: string;
  nonExposureHomework?: boolean;
  activitySteps?: string;
  alternative?: string;
  coachingPrompts?: string;
  points?: number;
  coins?: number;
  dueDate?: string;
  isRecurring?: boolean;
  repeats?: "Daily" | "Weekly" | "";
  repeatOn?: Weekday[];
  recurrenceEndDate?: string;
  completionStatus?: HomeworkCompletionStatus;
  summary?: string;
  firstSuds?: number | null;
  secondSuds?: number | null;
  sentimentAnalysis?: string;
  copingSkills?: string;
  altered?: string;
  routineTime?: string;
}

export interface HomeworkStepsUpdateFields {
  activitySteps?: string;
  alternative?: string;
  coachingPrompts?: string;
}

export type HomeworkImageUploadFields = ActivityTemplateImageUploadFields;

interface HomeworkImageMutationData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homeworkAssignmentId: string;
  error?: string;
}

export interface UploadHomeworkImageData extends HomeworkImageMutationData {
  operation: "portal.uploadHomeworkImage";
  uploaded: boolean;
  image: PortalActivityImage | null;
}

export interface DeleteHomeworkImageData extends HomeworkImageMutationData {
  operation: "portal.deleteHomeworkImage";
  deleted: boolean;
  imageId: string;
}

interface HomeworkUpdateMutationData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  updated: boolean;
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homework: PortalHomeworkDetail | null;
  error?: string;
}

export interface UpdateHomeworkData extends HomeworkUpdateMutationData {
  operation: "portal.updateHomework";
}

export interface UpdateHomeworkStepsData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  operation: "portal.updateHomeworkSteps";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  homework: ActivityStepsMutationRecord | null;
  error?: string;
}

export interface DuplicateHomeworkData {
  success: boolean;
  duplicated: boolean;
  authorized: boolean;
  operation: "portal.duplicateHomework";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  sourceHomeworkId: string;
  homework: PortalMutationHomework | null;
  activityImageCount: number;
  error?: string;
}

export interface DeleteHomeworkData {
  success: boolean;
  found: boolean;
  authorized: boolean;
  operation: "portal.deleteHomework";
  db: "supabase";
  therapist: PortalTherapist | null;
  patient: PortalPatient | null;
  deleted: boolean;
  homeworkAssignmentId: string;
  error?: string;
}

export interface ClinicianPortalApiRequest {
  apiVersion: "1";
  operation: PortalOperation;
  db: "supabase";
  clinicianCode: string;
  accessScope?: ClientAccessScope;
  therapistId?: string;
  question?: string;
  scope?: ClinicianAssistantScope;
  context?: ClinicianAssistantContext;
  history?: ClinicianAssistantHistoryMessage[];
  recordType?: "client";
  fieldKey?: "clinicId";
  catalogKey?: string;
  optionId?: string;
  key?: string;
  value?: string;
  colorKey?: string;
  sortOrder?: number;
  enabled?: boolean;
  isDefault?: boolean;
  collection?: string;
  assetPath?: string;
  patientId?: string;
  activityTemplateId?: string;
  includeHomeworkEngagement?: boolean;
  valueId?: string;
  valueName?: string;
  whyImportant?: string;
  ruleId?: string;
  rankLevelId?: string;
  thresholdPoints?: number;
  copingSkillId?: string;
  copingStrategyOptionId?: string;
  copingStrategyOptionIdProvided?: boolean;
  guideSteps?: string;
  guideStepsProvided?: boolean;
  nickname?: string;
  clinicId?: string;
  clinicalSummary?: string;
  highSudsThreshold?: number;
  sudsInputStyle?: string;
  selfInitiatedPermitted?: boolean;
  copingStrategies?: string[];
  funActivities?: string[];
  pointsEarned?: number;
  badgeCollection?: string[];
  homeworkAssignmentId?: string;
  homeworkName?: string;
  templateId?: string;
  imageId?: string;
  filename?: string;
  mimeType?: string;
  sizeBytes?: number;
  contentBase64?: string;
  valueSupportedId?: string;
  dueDate?: string;
  isRecurring?: boolean;
  repeats?: "Daily" | "Weekly" | "";
  repeatOn?: Weekday[];
  recurrenceEndDate?: string;
  completionStatus?: HomeworkCompletionStatus;
  summary?: string;
  firstSuds?: number | null;
  secondSuds?: number | null;
  sentimentAnalysis?: string;
  copingSkills?: string;
  altered?: string;
  routineTime?: string;
  sessionId?: string;
  sessionFilter?: ClientSessionFilter;
  pageSize?: number;
  pageOffset?: number;
  historyFilter?: HomeworkHistoryFilter;
  period?:
    | CaseloadEngagementPeriod
    | HomeworkOverviewPeriod
    | CompletionByDevicePeriod;
  startDate?: string;
  endDate?: string;
  clinicName?: string;
  therapistIds?: string[];
  therapistName?: string;
  email?: string;
  role?: AdminTherapistRole;
  clinicIds?: string[];
  advancedDashboardEnabled?: boolean;
  name?: string;
  description?: string;
  activityTypeId?: string;
  status?: string;
  nonExposureHomework?: boolean;
  activitySteps?: string;
  alternative?: string;
  coachingPrompts?: string;
  points?: number;
  coins?: number;
  tableKey?: PortalTableKey;
  columnOrder?: string[];
  hiddenColumns?: string[];
  columnWidths?: Record<string, number>;
  schemaVersion?: number;
}

export type ClinicianPortalErrorCode =
  | "INVALID_REQUEST"
  | "INVALID_CLIENT_CREATE_REQUEST"
  | "INVALID_CLIENT_UPDATE_REQUEST"
  | "REQUIRED_FIELD_MISSING"
  | "INVALID_LOV_VALUE"
  | "DISABLED_LOV_VALUE"
  | "INVALID_FIELD_TYPE"
  | "INVALID_INTEGER"
  | "VALUE_BELOW_MINIMUM"
  | "VALUE_ABOVE_MAXIMUM"
  | "INVALID_HIGH_SUDS_THRESHOLD"
  | "INVALID_POINTS"
  | "INVALID_COINS"
  | "NO_CLIENT_FIELDS_PROVIDED"
  | "FIELD_NOT_EDITABLE"
  | "CLINIC_NOT_AVAILABLE"
  | "INVALID_NICKNAME"
  | "INVALID_STATUS"
  | "INVALID_SUDS_INPUT_STYLE"
  | "INVALID_COPING_STRATEGIES"
  | "INVALID_FUN_ACTIVITIES"
  | "INVALID_PATIENT_UPDATE"
  | "INVALID_DATA_REQUEST"
  | "INVALID_RANK_LEVEL_ID"
  | "RANK_LEVEL_NOT_FOUND"
  | "RANK_LEVEL_DISABLED"
  | "INVALID_THRESHOLD_POINTS"
  | "NO_RANK_LEVEL_RULE_FIELDS_PROVIDED"
  | "DUPLICATE_CLIENT_RANK_LEVEL_RULE"
  | "INVALID_COPING_SKILL_ID"
  | "COPING_SKILL_NOT_FOUND"
  | "INVALID_COPING_STRATEGY_OPTION_ID"
  | "COPING_STRATEGY_OPTION_NOT_FOUND"
  | "COPING_STRATEGY_OPTION_DISABLED"
  | "INVALID_GUIDE_STEPS"
  | "NO_COPING_SKILL_FIELDS_PROVIDED"
  | "DUPLICATE_COPING_SKILL"
  | "DUPLICATE_CLIENT_COPING_SKILL"
  | "DUPLICATE_LOV_OPTION"
  | "DUPLICATE_CLINIC"
  | "INVALID_CLINIC_NAME"
  | "THERAPIST_REQUIRED"
  | "THERAPIST_NOT_FOUND"
  | "DUPLICATE_THERAPIST"
  | "INVALID_THERAPIST_NAME"
  | "INVALID_EMAIL"
  | "INVALID_ROLE"
  | "CLINIC_NOT_FOUND"
  | "ADMIN_REQUIRED"
  | "FORBIDDEN"
  | "NOT_AUTHORIZED"
  | "NOT_FOUND"
  | "TIMEOUT"
  | "UPSTREAM_ERROR";

export interface ClinicianPortalApiFailure {
  apiVersion: "1";
  operation: PortalOperation | "";
  data: null;
  error: {
    code: ClinicianPortalErrorCode;
    message: string;
    retryable: boolean;
  };
}

export interface ClinicianPortalApiSuccess<
  TData,
  TOperation extends PortalOperation,
> {
  apiVersion: "1";
  operation: TOperation;
  data: TData;
  error: null;
}

export type ClinicianPortalApiEnvelope<
  TData,
  TOperation extends PortalOperation,
> = ClinicianPortalApiSuccess<TData, TOperation> | ClinicianPortalApiFailure;
